import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import CategoryIcon from "@/components/CategoryIcon";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AdminAdsPanel } from "@/components/AdminAdsPanel";

const MACRO_ICONS = ["Home", "Car", "User", "Briefcase", "GraduationCap", "LayoutGrid",
  "Wrench", "Baby", "BookOpen", "Smartphone", "Hammer", "Ruler", "ShieldCheck", "Scissors", "Package", "Truck"];

const SAMPLE = `trade_name,category,subcats,city,lat,lng,phone,website,hours,fiscal_number
Idraulico Verona SRL,casa,idraulico|altro,Padova,45.4064,11.8768,+39049111222,,Lun-Ven 8-18,IT12345678901`;

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, catName, getCategory } = useApp();
  const [csv, setCsv] = useState("");
  const [result, setResult] = useState(null);
  const [sp, setSp] = useState({ business_id: "", category: "", city: "", days: 30 });
  const [stats, setStats] = useState(null);
  const [unclaimed, setUnclaimed] = useState([]);
  const [macros, setMacros] = useState([]);
  const [sponsorReqs, setSponsorReqs] = useState([]);
  const [bizQuery, setBizQuery] = useState("");
  const [bizList, setBizList] = useState([]);

  const loadSponsorReqs = () => API.get("/admin/sponsor-requests?status=pending").then((r) => setSponsorReqs(r.data)).catch(() => {});
  const loadBiz = (q = "") => API.get(`/admin/businesses${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setBizList(r.data)).catch(() => {});

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/"); return; }
    if (user && user.role === "admin") {
      API.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      API.get("/admin/unclaimed").then((r) => setUnclaimed(r.data)).catch(() => {});
      API.get("/macros").then((r) => setMacros(r.data)).catch(() => {});
      loadSponsorReqs();
      loadBiz();
    }
  }, [user]);

  const toggleVerify = async (b) => {
    try {
      await API.put(`/admin/businesses/${b.id}/verify`, { verified: !b.admin_verified });
      setBizList((prev) => prev.map((x) => (x.id === b.id ? { ...x, admin_verified: !x.admin_verified } : x)));
      toast.success(!b.admin_verified ? "Attività verificata" : "Verifica rimossa");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const approveSponsor = async (id) => {
    try { await API.post(`/admin/sponsor-requests/${id}/approve`); toast.success("Sponsor approvato e messo in evidenza"); loadSponsorReqs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const rejectSponsor = async (id) => {
    try { await API.post(`/admin/sponsor-requests/${id}/reject`); toast.success("Richiesta rifiutata"); loadSponsorReqs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const clearDemo = async () => {
    if (!window.confirm("Rimuovere tutti i dati dimostrativi (attività, richieste e account di esempio)? L'operazione è definitiva e non verranno ricreati.")) return;
    try {
      const r = await API.post("/admin/clear-demo");
      toast.success(`Fatto: rimosse ${r.data.removed_requests} richieste, ${r.data.removed_businesses} attività e ${r.data.removed_users} account demo.`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleMacroCat = (mid, slug) => setMacros((prev) => prev.map((m) =>
    m.id === mid ? { ...m, cats: m.cats.includes(slug) ? m.cats.filter((c) => c !== slug) : [...m.cats, slug] } : m));

  const setMacroLabel = (mid, label) => setMacros((prev) => prev.map((m) => (m.id === mid ? { ...m, label } : m)));

  const setMacroIcon = (mid, icon) => setMacros((prev) => prev.map((m) => (m.id === mid ? { ...m, icon } : m)));

  const saveMacros = async () => {
    try {
      await API.put("/admin/macros", { country: "IT", macros: macros.map((m) => ({ id: m.id, cats: m.cats, label: m.label, icon: m.icon })) });
      toast.success("Super-aree aggiornate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const doImport = async () => {
    try {
      const { data } = await API.post("/admin/businesses/import", { csv });
      setResult(data);
      toast.success(`Importate ${data.created}, saltate ${data.skipped}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const addSponsorship = async () => {
    if (!sp.business_id || !sp.category) return toast.error("ID attività e categoria obbligatori");
    try {
      await API.post("/admin/sponsorships", sp);
      toast.success("Sponsorizzazione creata");
      setSp({ business_id: "", category: "", city: "", days: 30 });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Layout>
      <h1 className="font-serif text-2xl font-bold text-[#0F1230] mb-4">Pannello admin</h1>

      {stats && (
        <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4" data-testid="admin-stats">
          <h2 className="font-semibold text-[#0F1230] mb-3">Statistiche · {stats.country}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Richieste" value={stats.total_requests} />
            <Stat label="Risolte" value={`${stats.pct_resolved}%`} />
            <Stat label="1ª disponibilità" value={stats.avg_first_availability_hours != null ? `${stats.avg_first_availability_hours} h` : "—"} />
            <Stat label="Attività" value={`${stats.total_businesses} (${stats.unclaimed} non riv.)`} />
          </div>
          <p className="text-sm font-medium text-[#0F1230] mb-2">Richieste per categoria</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(stats.requests_per_category).sort((a, b) => b[1] - a[1]).map(([slug, n]) => {
              const c = getCategory(slug);
              return <span key={slug} className="text-xs bg-[#EEF1F7] border border-[#DADFEA] rounded-full px-2.5 py-1">{c ? catName(c) : slug}: <b>{n}</b></span>;
            })}
          </div>
          {stats.most_active_users?.length > 0 && (
            <>
              <p className="text-sm font-medium text-[#0F1230] mb-1">Utenti più attivi</p>
              <ol className="text-sm text-[#2A2E48] list-decimal ml-5">
                {stats.most_active_users.map((u, i) => <li key={i}>{u.name} — {u.count} richieste</li>)}
              </ol>
            </>
          )}
        </div>
      )}

      {unclaimed.length > 0 && (
        <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4" data-testid="admin-unclaimed">
          <h2 className="font-semibold text-[#0F1230] mb-1">Schede non rivendicate più cercate</h2>
          <p className="text-xs text-[#4A4E68] mb-3">Contatta il titolare con i dati pubblici per invitarlo a rivendicare la scheda.</p>
          <div className="space-y-2">
            {unclaimed.slice(0, 10).map((b) => (
              <div key={b.id} className="flex items-center gap-2 border-b border-[#DADFEA] last:border-0 pb-2">
                <button onClick={() => navigate(`/business/${b.id}`)} className="text-left flex-1">
                  <span className="font-medium text-[#0F1230]">{b.trade_name}</span>
                  <span className="text-xs text-[#4A4E68]"> · {b.city} · {b.public_contacts?.phone || "n/d"}</span>
                </button>
                <span className="text-xs bg-[#FFF1E6] text-[#B8420A] rounded-full px-2 py-0.5 shrink-0">cercata {b.interest_count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sponsorReqs.length > 0 && (
        <div className="bg-white rounded-[18px] border border-[#FFD8BF] p-5 mb-4" data-testid="admin-sponsor-requests">
          <h2 className="font-semibold text-[#0F1230] mb-1">Richieste sponsor in attesa <span className="text-[#E8590C]">({sponsorReqs.length})</span></h2>
          <p className="text-xs text-[#4A4E68] mb-3">Attività che chiedono di essere messe in evidenza. Approva per pubblicarle (crea la scheda se non esiste) o rifiuta.</p>
          <div className="space-y-3">
            {sponsorReqs.map((s) => {
              const c = getCategory(s.category);
              return (
                <div key={s.id} data-testid={`sponsor-req-${s.id}`} className="border border-[#F1F1F7] rounded-xl p-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-semibold text-[#0F1230]">{s.business_name}</span>
                    <span className="text-xs bg-[#EEF1F7] border border-[#DADFEA] rounded-full px-2 py-0.5">{c ? catName(c) : s.category}</span>
                    {s.city && <span className="text-xs text-[#4A4E68]">{s.city}</span>}
                    <span className="text-xs text-[#4A4E68]">· {s.duration_days} giorni</span>
                  </div>
                  <p className="text-xs text-[#4A4E68] mt-1">
                    {[s.contact_name, s.contact_phone, s.contact_email, s.website].filter(Boolean).join(" · ") || "Nessun recapito"}
                  </p>
                  {s.note && <p className="text-sm text-[#2A2E48] mt-1">“{s.note}”</p>}
                  <div className="flex gap-2 mt-2">
                    <Button data-testid={`sponsor-approve-${s.id}`} onClick={() => approveSponsor(s.id)} size="sm" className="rounded-full bg-[#1F4FE0] text-white">Approva</Button>
                    <Button data-testid={`sponsor-reject-${s.id}`} onClick={() => rejectSponsor(s.id)} size="sm" variant="outline" className="rounded-full">Rifiuta</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-[18px] border border-[#FFD8BF] p-5 mb-4" data-testid="admin-clear-demo">
        <h2 className="font-semibold text-[#0F1230] mb-1">Pronto per il lancio</h2>
        <p className="text-xs text-[#4A4E68] mb-3">Rimuove i contenuti di esempio (attività, richieste e account dimostrativi) creati per la demo. Il tuo account admin e i dati reali restano, e i dati demo non verranno più ricreati.</p>
        <Button data-testid="clear-demo-button" onClick={clearDemo} className="rounded-full bg-[#E8590C] hover:bg-[#B8420A] text-white">Rimuovi dati dimostrativi</Button>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4" data-testid="admin-macros">
        <h2 className="font-semibold text-[#0F1230] mb-1">Super-aree (macro) della Home</h2>
        <p className="text-xs text-[#4A4E68] mb-3">Modifica nome, icona e categorie di ciascuna super-area (mostrate in {'"Ho bisogno"'}).</p>
        <div className="space-y-5">
          {macros.map((m) => (
            <div key={m.id} data-testid={`macro-row-${m.id}`} className="border-b border-[#F1F1F7] pb-4 last:border-0">
              <Input data-testid={`macro-label-${m.id}`} value={m.label} onChange={(e) => setMacroLabel(m.id, e.target.value)}
                className="mb-2 max-w-xs font-semibold" placeholder="Nome super-area" />
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`macro-icons-${m.id}`}>
                {MACRO_ICONS.map((ic) => (
                  <button key={ic} data-testid={`macro-${m.id}-icon-${ic}`} onClick={() => setMacroIcon(m.id, ic)} title={ic}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center border ${m.icon === ic ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "bg-white text-[#2A2E48] border-[#DADFEA]"}`}>
                    <CategoryIcon name={ic} size={18} />
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => {
                  const on = m.cats.includes(c.slug);
                  return (
                    <button key={c.slug} data-testid={`macro-${m.id}-cat-${c.slug}`} onClick={() => toggleMacroCat(m.id, c.slug)}
                      className={`px-3 h-9 rounded-full text-sm border ${on ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] text-[#2A2E48]"}`}>{catName(c)}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="macros-save" onClick={saveMacros} className="mt-4 bg-[#1F4FE0] text-white rounded-full">Salva super-aree</Button>
      </div>

      <AdminAdsPanel />

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4" data-testid="admin-locandina">
        <h2 className="font-semibold text-[#0F1230] mb-1">Locandina con QR da stampare</h2>
        <p className="text-xs text-[#4A4E68] mb-3">Crea una locandina con il QR code verso fydate.it, da stampare e appendere in negozi, bar e bacheche o da condividere nei gruppi.</p>
        <Button data-testid="open-locandina" onClick={() => navigate("/locandina")} className="rounded-full bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230]">Apri la locandina</Button>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4" data-testid="admin-verify">
        <h2 className="font-semibold text-[#0F1230] mb-1">Attività verificate</h2>
        <p className="text-xs text-[#4A4E68] mb-3">Assegna il badge <b>Attività verificata</b> alle schede affidabili. Il badge compare accanto al nome in Cerca e nelle schede.</p>
        <div className="flex gap-2 mb-3">
          <Input data-testid="verify-search" value={bizQuery} onChange={(e) => setBizQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadBiz(bizQuery)} placeholder="Cerca per nome o città" className="h-10" />
          <Button data-testid="verify-search-btn" onClick={() => loadBiz(bizQuery)} variant="outline" className="rounded-full shrink-0">Cerca</Button>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {bizList.length === 0 ? <p className="text-sm text-[#4A4E68]">Nessuna attività.</p> : bizList.map((b) => {
            const c = getCategory(b.category);
            return (
              <div key={b.id} data-testid={`verify-row-${b.id}`} className="flex items-center gap-2 border-b border-[#F1F1F7] last:border-0 pb-2">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-[#0F1230]">{b.trade_name}</span>
                  <span className="text-xs text-[#4A4E68]"> · {c ? catName(c) : b.category}{b.city ? ` · ${b.city}` : ""}</span>
                </div>
                <Button data-testid={`verify-toggle-${b.id}`} size="sm" onClick={() => toggleVerify(b)}
                  className={`rounded-full shrink-0 ${b.admin_verified ? "bg-[#E8F5E9] text-[#2E7D32] hover:bg-[#d8eeda]" : "bg-[#1F4FE0] text-white hover:bg-[#2E43A8]"}`}>
                  {b.admin_verified ? "✓ Verificata" : "Verifica"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 mb-4">
        <h2 className="font-semibold text-[#0F1230] mb-2">Importa attività da CSV</h2>
        <p className="text-xs text-[#4A4E68] mb-2">Colonne: trade_name, category, subcats (separati da |), city, lat, lng, phone, website, hours, fiscal_number. I doppioni (stesso nome + città) vengono saltati.</p>
        <Textarea data-testid="csv-input" rows={7} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={SAMPLE} className="font-mono text-xs" />
        <div className="flex gap-2 mt-3">
          <Button data-testid="csv-sample" variant="outline" className="rounded-full" onClick={() => setCsv(SAMPLE)}>Usa esempio</Button>
          <Button data-testid="csv-import" onClick={doImport} disabled={!csv.trim()} className="bg-[#1F4FE0] text-white rounded-full">Importa</Button>
        </div>
        {result && <p data-testid="import-result" className="text-sm text-[#2E7D32] mt-2">Importate: {result.created} · Saltate (doppioni): {result.skipped}</p>}
      </div>

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5">
        <h2 className="font-semibold text-[#0F1230] mb-2">{'Spazio "In evidenza" (sponsorizzato)'}</h2>
        <p className="text-xs text-[#4A4E68] mb-3">{"Mostra un'attività in evidenza per categoria e zona, separata e segnata. Non modifica l'ordine delle schede normali."}</p>
        <div className="space-y-3">
          <div><Label>ID attività (business id)</Label><Input data-testid="sp-business" value={sp.business_id} onChange={(e) => setSp({ ...sp, business_id: e.target.value })} className="mt-1" /></div>
          <div>
            <Label>Categoria</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {categories.filter((c) => c.slug !== "varie").map((c) => (
                <button key={c.slug} data-testid={`sp-cat-${c.slug}`} onClick={() => setSp({ ...sp, category: c.slug })} className={`px-3 h-9 rounded-full text-sm border ${sp.category === c.slug ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{catName(c)}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Città</Label><Input data-testid="sp-city" value={sp.city} onChange={(e) => setSp({ ...sp, city: e.target.value })} className="mt-1" /></div>
            <div><Label>Giorni</Label><Input data-testid="sp-days" type="number" value={sp.days} onChange={(e) => setSp({ ...sp, days: parseInt(e.target.value) || 30 })} className="mt-1" /></div>
          </div>
          <Button data-testid="sp-submit" onClick={addSponsorship} className="w-full h-11 bg-[#1F4FE0] text-white rounded-full">Crea sponsorizzazione</Button>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#EEF1F7] rounded-xl p-3 text-center">
      <div className="text-xl font-serif font-bold text-[#141A3A]">{value}</div>
      <div className="text-xs text-[#4A4E68]">{label}</div>
    </div>
  );
}
